let webGPUContext = null;
let webGPUInitializationPromise = null;
/**
 * 初始化WebGPU（单例模式）
 * @returns WebGPU上下文
 */
export async function initializeWebGPU() {
    // 如果已经初始化，直接返回
    if (webGPUContext) {
        return webGPUContext;
    }
    // 如果正在初始化，等待完成
    if (webGPUInitializationPromise) {
        return webGPUInitializationPromise;
    }
    // 开始初始化
    webGPUInitializationPromise = performWebGPUInitialization();
    try {
        webGPUContext = await webGPUInitializationPromise;
        return webGPUContext;
    }
    catch (error) {
        // 初始化失败，重置状态
        webGPUInitializationPromise = null;
        throw error;
    }
}
/**
 * 执行WebGPU初始化
 * @returns WebGPU上下文
 */
async function performWebGPUInitialization() {
    if (!navigator.gpu) {
        throw new Error('WebGPU 不支持');
    }
    const adapter = await navigator.gpu.requestAdapter();
    if (!adapter) {
        throw new Error('无法获取 WebGPU 适配器');
    }
    // 配置设备限制，支持更大的工作组
    const requiredLimits = {
        maxComputeInvocationsPerWorkgroup: 1024, // 支持32x32工作组
        maxComputeWorkgroupSizeX: 32,
        maxComputeWorkgroupSizeY: 32,
        maxComputeWorkgroupSizeZ: 64
    };
    const device = await adapter.requestDevice({ requiredLimits });
    if (!device) {
        throw new Error('无法创建 WebGPU 设备');
    }
    // 创建离屏画布用于纹理操作
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('webgpu');
    if (!context) {
        throw new Error('无法创建 WebGPU 画布上下文');
    }
    return {
        device,
        context,
        canvas
    };
}
/**
 * 检查WebGPU支持（单例模式）
 * @returns WebGPU支持信息
 */
export async function checkWebGPUSupport() {
    try {
        // 尝试初始化WebGPU
        await initializeWebGPU();
        return { supported: true };
    }
    catch (error) {
        return {
            supported: false,
            error: error instanceof Error ? error.message : '未知错误'
        };
    }
}
/**
 * 获取WebGPU设备能力信息
 * @returns 设备能力信息
 */
export async function getWebGPUDeviceInfo() {
    try {
        const context = await initializeWebGPU();
        // 获取适配器名称（如果可用）
        let adapterName;
        try {
            // 使用类型断言访问适配器名称
            const adapterAny = context.device;
            if (adapterAny.adapter && adapterAny.adapter.name) {
                adapterName = adapterAny.adapter.name;
            }
        }
        catch (e) {
            // 忽略适配器名称获取错误
        }
        // 获取设备限制
        const limits = {};
        try {
            // 使用类型断言访问设备限制
            const deviceAny = context.device;
            if (deviceAny.limits) {
                const limitsObj = deviceAny.limits;
                for (const key in limitsObj) {
                    if (typeof limitsObj[key] === 'number') {
                        limits[key] = limitsObj[key];
                    }
                }
            }
        }
        catch (e) {
            // 忽略限制获取错误
        }
        return {
            supported: true,
            ...(adapterName && { adapterName }),
            ...(Object.keys(limits).length > 0 && { limits })
        };
    }
    catch (error) {
        return {
            supported: false,
            error: error instanceof Error ? error.message : '未知错误'
        };
    }
}
/**
 * 检查工作组大小支持
 * @param workgroupSize 要检查的工作组大小
 * @returns 是否支持该工作组大小
 */
export async function checkWorkgroupSizeSupport(workgroupSize) {
    try {
        const deviceInfo = await getWebGPUDeviceInfo();
        if (!deviceInfo.supported || !deviceInfo.limits) {
            return false;
        }
        // 检查最大计算工作组大小
        const maxComputeWorkgroupSizeX = deviceInfo.limits.maxComputeWorkgroupSizeX || 256;
        const maxComputeWorkgroupSizeY = deviceInfo.limits.maxComputeWorkgroupSizeY || 256;
        const maxComputeWorkgroupSizeZ = deviceInfo.limits.maxComputeWorkgroupSizeZ || 64;
        const maxComputeInvocationsPerWorkgroup = deviceInfo.limits.maxComputeInvocationsPerWorkgroup || 256;
        console.log('WebGPU设备限制:', {
            maxComputeWorkgroupSizeX,
            maxComputeWorkgroupSizeY,
            maxComputeWorkgroupSizeZ,
            maxComputeInvocationsPerWorkgroup
        });
        // 检查2D工作组大小是否支持
        return workgroupSize <= maxComputeWorkgroupSizeX &&
            workgroupSize <= maxComputeWorkgroupSizeY &&
            workgroupSize * workgroupSize <= maxComputeInvocationsPerWorkgroup;
    }
    catch (error) {
        console.warn('无法检查工作组大小支持:', error);
        return false;
    }
}
//# sourceMappingURL=setup.js.map